// @ts-nocheck
/* eslint-disable camelcase */

import _ from 'lodash';

import { getRange, getAverage, addRange, Butterworth } from './pantompkin';

import { BlocksInterest } from './blocksInterest';

/**
 * Approximates the sample-by-sample first derivative of an ECG sample array so QRS slopes can be measured. Uses a forward difference at the first index, the average of the backward and forward differences for interior indices up to nx-2, and a backward difference at the last index.
 *
 * @param data - ECG sample array to differentiate.
 * @param nx - Number of samples to process, sizing the output array.
 * @returns Array of derivative values, one per sample index.
 */
export const FirstDerivative = function (data: any, nx: any) {
  let i;
  let tmp1;
  let tmp2;
  const firstderv = Array(nx);
  // compute derivative first index
  firstderv[0] = data[1] - data[0];

  for (i = 1; i < nx - 2; i += 1) {
    tmp1 = data[i] - data[i - 1];
    tmp2 = data[i + 1] - data[i];
    firstderv[i] = (tmp2 + tmp1) / 2;
  }

  // compute derivative last index
  firstderv[nx - 1] = data[nx - 1] - data[nx - 2];
  return firstderv;
};

/**
 * Removes baseline wander from an ECG segment and derives the enhanced signals and candidate P and T interest blocks for PQRST delineation. Subtracts a two-stage moving-average baseline (0.2 s then 0.6 s), Butterworth-filters at 17, 10 and 30 Hz, takes the first derivative, applies a mask enhancing Q and S, blanks the QRS around each annotated R peak, then scans the remaining intervals with moving-average envelopes for P and T blocks, preferring a negative T exceeding the positive one by 0.2.
 *
 * @param qrslocation - Sample positions of the detected R peaks inside the segment.
 * @param ecgData - Raw ECG samples of the segment.
 * @param fs - Sampling rate in Hz, used to size every filter window.
 * @returns Object holding the P and T interest blocks, the QRS-enhanced signal, the 30 Hz filtered signal, the moving-average peak and wave envelopes, the first derivative, and the baseline-corrected signal.
 */
export const NewBaseLineAndEnhancement = function (qrslocation: any, ecgData: any, fs: any) {
  const N = ecgData.length;

  const blocksT = [];
  const blocksP = [];
  const flen1 = Math.floor((fs * 0.2) / 2);
  const flen2 = Math.floor((fs * 0.6) / 2);
  // baseline wander remove
  const b1 = Array(N);
  const nor = Array(N);
  const bwr = Array(N);
  let start;
  let stop;
  for (let j = 0; j < N; j += 1) {
    start = Math.max(j - flen1, 0);
    stop = Math.min(j + flen1, N);
    let mean = 0;
    for (let i = start; i < stop; i += 1) {
      mean += ecgData[i];
    }
    b1[j] = mean / (stop - start);
  }
  for (let j = 0; j < N; j += 1) {
    start = Math.max(j - flen2, 0);
    stop = Math.min(j + flen2, N);
    let mean = 0;
    for (let i = start; i < stop; i += 1) {
      mean += b1[i];
    }
    nor[j] = mean / (stop - start);
    bwr[j] = (Math.round as any)(ecgData[j] - nor[j], 4);
  }

  let filtered15 = Array(N);
  filtered15 = Butterworth(bwr, fs, 17);
  const qrsEnhancement = Array(N);
  const firstDerivative = FirstDerivative(filtered15, N);

  // Applying an enhancement mask to the filtered signal - Q+S detection
  const K = 5;
  for (let m = 0; m < N - K; m += 1) {
    let S = 0.0;
    let M = 0;
    for (let j = -K; j <= K; j += 1) {
      const n = K + j;
      if (n >= 0 && n <= K - 1) {
        M = -1;
      } else if (n === K) {
        M = 2 * K;
      } else if (n >= K + 1 && n <= 2 * K) {
        M = -1;
      } else {
        M = 0;
      }

      S += M * filtered15[Math.max(m + j, 0)];
    }
    qrsEnhancement[m] = S;
  }

  let yN = Array(N);

  yN = Butterworth(bwr, fs, 10);
  // QRS Removal
  let before_R_peak;
  let after_R_peak;
  before_R_peak = parseInt((0.083 * fs) as any, 10);
  after_R_peak = parseInt((0.116 * fs) as any, 10);
  const lstQRSEnhancement = qrsEnhancement;
  for (let ri = 1; ri < qrslocation.length - 1; ri += 1) {
    if (qrslocation[ri] === 0 || qrslocation[ri - 1] === 0 || qrslocation[ri + 1] === 0) {
      // oxlint-disable-next-line no-continue
      continue;
    }
    const tmpRangeBefore = getRange(
      lstQRSEnhancement,
      qrslocation[ri] - (qrslocation[ri] - qrslocation[ri - 1]) / 2,
      Math.min(
        (qrslocation[ri] - qrslocation[ri - 1]) / 2,
        Math.max(
          lstQRSEnhancement.length -
            (qrslocation[ri] - (qrslocation[ri] - qrslocation[ri - 1]) / 2),
          0,
        ),
      ),
    );
    if (
      tmpRangeBefore &&
      tmpRangeBefore.length > 0 &&
      _.indexOf(tmpRangeBefore, _.min(tmpRangeBefore)) > 0
    ) {
      before_R_peak =
        qrslocation[ri] -
        Math.min(
          (qrslocation[ri] - qrslocation[ri - 1]) / 2 +
            _.indexOf(tmpRangeBefore, _.min(tmpRangeBefore)) +
            (qrslocation[ri] - qrslocation[ri - 1]) / 20,
          parseInt((0.083 * fs) as any, 10),
        );
    } else {
      before_R_peak = qrslocation[ri] - parseInt((0.083 * fs) as any, 10);
    }

    const tmpRangeAfter = getRange(
      lstQRSEnhancement,
      qrslocation[ri],
      Math.min(
        (qrslocation[ri + 1] - qrslocation[ri]) / 2,
        Math.max(lstQRSEnhancement.length - qrslocation[ri], 0),
      ),
    );
    if (
      tmpRangeAfter &&
      tmpRangeAfter.length > 0 &&
      _.indexOf(tmpRangeAfter, _.min(tmpRangeAfter)) > 0
    ) {
      after_R_peak =
        qrslocation[ri] +
        Math.min(
          _.indexOf(tmpRangeAfter, _.min(tmpRangeAfter)) +
            (qrslocation[ri + 1] - qrslocation[ri]) / 20,
          parseInt((0.116 * fs) as any, 10),
        );
    } else {
      after_R_peak = qrslocation[ri] + parseInt((0.116 * fs) as any, 10);
    }

    for (let i = Math.max(before_R_peak, 0); i < Math.min(after_R_peak, N); i += 1) {
      yN[i] = 0;
    }
  }
  const MApeakN = Array(N);
  const MAwaveN = Array(N);
  const Blocks = Array(N);
  const BlocksNegative = Array(N);

  let W1 = Math.floor(0.055 * fs);
  if (W1 % 2 === 0) {
    W1 += 1;
  }
  for (let n = 0; n < N; n += 1) {
    start = Math.max(n - (W1 - 1) / 2, 0);
    stop = Math.min(n + (W1 - 1) / 2, N);
    let mean = 0;
    for (let i = start; i < stop; i += 1) {
      mean += yN[i];
    }
    MApeakN[n] = mean / (stop - start);
  }
  // Second moving average: The purpose of the second moving average(MAPwave)
  // is to be used as a threshold for the first moving average MAPeak
  let W2 = Math.floor(0.11 * fs);
  if (W2 % 2 === 0) {
    W2 += 1;
  }
  for (let n = 0; n < N; n += 1) {
    start = Math.max(n - (W2 - 1) / 2, 0);
    stop = Math.min(n + (W2 - 1) / 2, N);
    let mean = 0;
    for (let i = start; i < stop; i += 1) {
      mean += yN[i];
    }
    MAwaveN[n] = mean / (stop - start);
    if (MApeakN[n] - MAwaveN[n] > 0.008 && MApeakN[n] !== 0) {
      Blocks[n] = 0.25;
    } else {
      Blocks[n] = 0.0;
    }

    if (MAwaveN[n] - MApeakN[n] > 0.02 && MApeakN[n] < 0) {
      BlocksNegative[n] = 0.25;
    } else {
      BlocksNegative[n] = 0.0;
    }
  }

  for (let n = 0; n < qrslocation[1]; n += 1) {
    Blocks[n] = 0.0;
    BlocksNegative[n] = 0.0;
    MApeakN[n] = 0.0;
    MAwaveN[n] = 0.0;
  }

  for (let n = qrslocation[qrslocation.length - 1]; n < N; n += 1) {
    Blocks[n] = 0.0;
    BlocksNegative[n] = 0.0;
    MApeakN[n] = 0.0;
    MAwaveN[n] = 0.0;
  }

  const lstMApeakN = MApeakN;

  for (let i = 0; i < qrslocation.length - 1; i += 1) {
    let block_size = 0;
    let block_size_negative = 0;
    start = qrslocation[i];
    stop = qrslocation[i + 1];
    const RTmin = parseInt(((stop - start) * 0.111) as any, 10);
    const RTmax = parseInt(((stop - start) * 0.583) as any, 10);

    const PRmin = parseInt((0.055 * fs) as any, 10);
    const PRmax = parseInt((0.47 * fs) as any, 10);
    let block = new BlocksInterest(0, 0, 0, 0, 0, 0, 0, 0.0, 0.0);
    let blockNegative = new BlocksInterest(0, 0, 0, 0, 0, 0, 0, 0.0, 0.0);
    const blocks_acceptable_negative: any = [];
    const blocks_acceptable = [];
    const blocksT_acceptable: any = [];
    const blocksP_acceptable: any = [];

    let countBlockAccept = 0;
    let countBlockAcceptNegative = 0;

    let tAcceptIndex = -1;
    let pAcceptIndex = -1;

    for (let j = start; j < stop && j < N;) {
      if (Blocks[j] === 0.25) {
        block_size += 1;
        if (block.start === 0) {
          block.start = j;
        }
      } else if (block_size > 0 && block_size <= W1 / 2) {
        while (block_size > 0) {
          Blocks[j - block_size] = 0;
          block_size -= 1;
        }
        block = new BlocksInterest(0, 0, 0, 0, 0, 0, 0, 0.0, 0.0);
      } else if (block_size > W1 / 2) {
        if (block.stop === 0) {
          block.stop = j;
        }

        const rangeLstMApeakN = getRange(
          lstMApeakN,
          block.start,
          Math.min(block.stop - block.start, Math.max(lstMApeakN.length - block.start, 0)),
        );

        const indexMaximumPoint = block.start + _.indexOf(rangeLstMApeakN, _.max(rangeLstMApeakN));

        block.rLeftmin = block.start - start;
        block.rLeftmax = block.stop - start;
        block.distanceT = indexMaximumPoint - start;

        block.rRightmin = stop - block.stop;
        block.rRightmax = stop - block.start;
        block.distanceP = stop - indexMaximumPoint;

        block.indexPeakWave = indexMaximumPoint;

        block.rrDuration = stop - start;

        block.AmplitudeTWave = MApeakN[indexMaximumPoint];
        block.AmplitudePWave = 1.0;

        blocks_acceptable.push(block);
        countBlockAccept += 1;
        block_size = 0;
        block = new BlocksInterest(0, 0, 0, 0, 0, 0, 0, 0.0, 0.0);
      }

      if (BlocksNegative[j] === 0.25) {
        block_size_negative += 1;
        if (blockNegative.start === 0) {
          blockNegative.start = j;
        }
      } else if ((block_size_negative > 0 && block_size_negative <= W1) || block_size >= W2) {
        while (block_size_negative > 0) {
          BlocksNegative[j - block_size_negative] = 0;
          block_size_negative -= 1;
        }
        blockNegative = new BlocksInterest(0, 0, 0, 0, 0, 0, 0, 0.0, 0.0);
      } else if (block_size_negative > W1 && block_size < W2) {
        if (blockNegative.stop === 0) {
          blockNegative.stop = j;
        }

        const rangeLstMApeakN = getRange(
          lstMApeakN,
          blockNegative.start,
          Math.min(
            blockNegative.stop - blockNegative.start,
            Math.max(lstMApeakN.length - blockNegative.start, 0),
          ),
        );
        const indexMaximumPoint =
          blockNegative.start + _.indexOf(rangeLstMApeakN, _.min(rangeLstMApeakN));

        blockNegative.rLeftmin = blockNegative.start - start;
        blockNegative.rLeftmax = blockNegative.stop - start;
        blockNegative.distanceT = indexMaximumPoint - start;

        blockNegative.rRightmin = stop - blockNegative.stop;
        blockNegative.rRightmax = stop - blockNegative.start;
        blockNegative.distanceP = stop - indexMaximumPoint;

        blockNegative.indexPeakWave = indexMaximumPoint;

        blockNegative.rrDuration = stop - start;

        blockNegative.AmplitudeTWave = MApeakN[indexMaximumPoint];
        blockNegative.AmplitudePWave = -1.0;

        blocks_acceptable_negative.push(blockNegative);
        countBlockAcceptNegative += 1;
        block_size_negative = 0;
        blockNegative = new BlocksInterest(0, 0, 0, 0, 0, 0, 0, 0.0, 0.0);
      }

      j += 1;
    }

    if (countBlockAccept === 1) {
      if (blocks_acceptable[0].distanceT > RTmin && blocks_acceptable[0].distanceT < RTmax) {
        tAcceptIndex = blocksT.length;
        blocksT.push(blocks_acceptable[0]);
      } else if (blocks_acceptable[0].distanceP > PRmin && blocks_acceptable[0].distanceP < PRmax) {
        if (MAwaveN[blocks_acceptable[0].start] - MAwaveN[blocks_acceptable[0].stop] < 0.0) {
          pAcceptIndex = blocksP.length;
          blocksP.push(blocks_acceptable[0]);
        }
      } else {
        tAcceptIndex = blocksT.length;
        blocksT.push(blocks_acceptable[0]);
      }
    } else if (countBlockAccept >= 2) {
      // T
      _.forEach(blocks_acceptable, (bl) => {
        if (bl.distanceT > RTmin && bl.distanceT < RTmax) {
          blocksT_acceptable.push(bl);
        }
      });
      const max: any = _.maxBy(blocksT_acceptable, 'AmplitudeTWave');
      const t_acceptable: any = _.find(
        blocksT_acceptable,
        (t) => t.AmplitudeTWave === max.AmplitudeTWave,
      );
      // const t_acceptable = _.find(blocksT_acceptable, t => t.AmplitudeTWave === max);

      if (t_acceptable) {
        block_size -= 1;
        tAcceptIndex = blocksT.length;
        blocksT.push(t_acceptable);
      }

      // P
      _.forEach(blocks_acceptable, (bl) => {
        if (bl.distanceP > PRmin && bl.distanceP < PRmax) {
          if (t_acceptable && bl.start > t_acceptable.stop) {
            blocksP_acceptable.push(bl);
          } else if (t_acceptable === null) {
            blocksP_acceptable.push(bl);
          }
        }
      });

      if (blocksP_acceptable.length > 0) {
        const p_acceptable: any = blocksP_acceptable[blocksP_acceptable.length - 1];
        if (p_acceptable !== null && typeof p_acceptable !== 'undefined') {
          pAcceptIndex = blocksP.length;
          blocksP.push(p_acceptable);
        }
      }
    }

    if (countBlockAcceptNegative > 0 && tAcceptIndex > 0) {
      const tAcceptBlock: any = blocksT[tAcceptIndex];
      const t_negative_acceptable: any = _.find(blocks_acceptable_negative, (t) => {
        const tmp: any = Math.abs(t.AmplitudeTWave) - Math.abs(tAcceptBlock.AmplitudeTWave);
        return tmp > 0.2;
      });

      if (t_negative_acceptable) {
        blocksT.splice(tAcceptIndex, 1);
        blocksT.push(t_negative_acceptable);

        if (pAcceptIndex > 0 && blocksP[pAcceptIndex].start - t_negative_acceptable.stop < W1) {
          blocksP.splice(pAcceptIndex, 1);
        }
      }
    } else if (countBlockAcceptNegative > 0 && tAcceptIndex < 0) {
      /**
       * Selects the negative interest block whose T-wave amplitude equals the minimum across all negative blocks. Uses lodash find comparing each AmplitudeTWave against the minBy result.
       *
       * @returns The most negative T-wave block, or undefined.
       */
      const t_negative_acceptable = _.find(
        blocks_acceptable_negative,
        (t): any =>
          t.AmplitudeTWave === _.minBy(blocks_acceptable_negative, (a) => a.AmplitudeTWave),
      );
      if (t_negative_acceptable !== null && typeof t_negative_acceptable !== 'undefined') {
        blocksT.push(t_negative_acceptable);

        if (pAcceptIndex > 0 && blocksP[pAcceptIndex].start - t_negative_acceptable.stop < W1) {
          blocksP.splice(pAcceptIndex, 1);
        }
      }
    }
  }
  // P, T,
  const filter = Butterworth(bwr, fs, 30);
  return {
    blocksP,
    blocksT,
    qrsEnhancement,
    filter,
    MApeakN,
    MAwaveN,
    firstDerivative,
    bwr,
  };
};

/**
 * Locates the P, Q, R, S, T and next-R sample positions for the beat nearest a click on an ECG strip. Converts the click offset to a sample index via samplingRate, wndDuration and width, slices the display window plus the beat annotations inside it, runs baseline removal and enhancement for the filtered signal, first derivative and P/T blocks, bounds Q and S outward from the closest R, fixes P onset and T end by maximum trapezium area, then shifts each index by the window start and clamps it.
 *
 * @param windowLength - Number of samples of the analysis window centred on the click.
 * @param pointerX - Horizontal pointer position within the strip, in pixels.
 * @param clickedChannel - Index of the ECG channel that was clicked.
 * @param data - Strip context supplying config (samplingRate, wndDuration, width), offsetX, ECGBuffer and ANNBuffer.
 * @returns Object with P, Q, R, S, T and R2 sample indices, all null when the window contains no beat annotation.
 */
export const getPQSTObject = function (
  windowLength: any,
  pointerX: any,
  clickedChannel: any,
  data: any,
) {
  const resultObj: any = {
    P: null,
    Q: null,
    R: null,
    S: null,
    T: null,
    R2: null,
  };
  const spp = (data.config.samplingRate * data.config.wndDuration) / data.config.width;
  const pointXClick = data.offsetX + pointerX * spp;
  const lastIndex = data.ECGBuffer[clickedChannel].length;

  const startSampleDisplay = Math.max(Math.round(pointXClick - windowLength / 2), 0);
  /**
   * Extracts the slice of the clicked channel's ECG buffer covering the visible display window. Ranges from startSampleDisplay for windowLength samples, clamped to the channel buffer length.
   *
   * @returns The ECG samples within the visible window.
   */
  const ECGViewBuffer = getRange(
    data.ECGBuffer[clickedChannel],
    startSampleDisplay,
    Math.min(startSampleDisplay + windowLength, data.ECGBuffer[clickedChannel].length) -
      startSampleDisplay,
  );
  /**
   * Selects the beat-annotation sample positions that fall inside the currently displayed ECG window. Filters data.ANNBuffer to values at or after startSampleDisplay and before the window end clamped to the channel buffer length.
   *
   * @returns Annotation sample positions within the visible window.
   */
  const ANNViewBuffer = _.filter(
    data.ANNBuffer,
    (x) =>
      x >= startSampleDisplay &&
      x < Math.min(startSampleDisplay + windowLength, data.ECGBuffer[clickedChannel].length),
  );

  if (ANNViewBuffer.length > 0) {
    // const ecgData = [];
    const qrsData: any = [];

    _.forEach(ANNViewBuffer, (aPos) => {
      // qrsData.push(parseInt((aPos - startSampleDisplay) * data.config.samplingRate, 10));
      qrsData.push(parseInt((aPos - startSampleDisplay) as any, 10));
    });
    // _.forEach(ECGViewBuffer, (s) => {
    //   ecgData.push(s);
    // });

    const { blocksP, blocksT, qrsEnhancement, filter, firstDerivative, bwr }: any =
      NewBaseLineAndEnhancement(qrsData, ECGViewBuffer, data.config.samplingRate);

    const study: any = {
      EnhancementQRS: [],
      BWR: [],
      FirstDerivative: [],
      Filter: [],
      BlockPwave: blocksP,
      BlockTwave: blocksT,
    };
    addRange(study.Filter, filter);
    addRange(study.BWR, bwr);
    addRange(study.EnhancementQRS, qrsEnhancement);
    addRange(study.FirstDerivative, firstDerivative);

    let P = -1;
    let Q = -1;
    let S = -1;
    let T = -1;
    let R = -1;

    let pointP = null;
    let pointT = null;
    let T_Tend = -1;
    let P_Tend = -1;
    let S_point = -1;
    let S_Tend = -1;
    let Q_point = -1;
    let Q_Tend = -1;

    // const pointX = parseInt((pointXClick - startSampleDisplay) * data.config.samplingRate, 10);
    const pointX = parseInt((pointXClick - startSampleDisplay) as any, 10);
    qrsData.unshift(0);
    // qrsData.push(parseInt(windowLength * data.config.samplingRate, 10));
    qrsData.push(parseInt(windowLength, 10));

    // const closestR = qrsData.OrderBy(item => Math.Abs(pointX - item)).First(); // Find the minimum distance
    const closestR = _.sortBy(qrsData, [(item) => Math.abs(pointX - item)])[0];

    const beforeClosestR = qrsData[Math.max(_.indexOf(qrsData, closestR) - 1, 0)];
    const afterClosestR = qrsData[Math.min(_.indexOf(qrsData, closestR) + 1, qrsData.length - 1)];

    /** *
     * S Point Detection
     * */

    const window_fiducial_point = parseInt((0.12 * data.config.samplingRate) as any, 10);
    const QRS_segment_range = getRange(
      study.EnhancementQRS,
      closestR - window_fiducial_point,
      window_fiducial_point * 2,
    );
    // const maximum_amplitude = _.max(QRS_segment_range);
    const average_amplitude = getAverage(QRS_segment_range);
    const QRS_haft_segment_range = getRange(
      QRS_segment_range,
      window_fiducial_point,
      window_fiducial_point,
    );

    /** *
     * The S point is assigned as the point of amplitude less than the average amplitude with a local
     * minimum slope after the S start searching point.
     * */

    const s_segment_range = getRange(QRS_haft_segment_range, 0, window_fiducial_point);

    if (
      s_segment_range &&
      s_segment_range.length > 0 &&
      _.min(s_segment_range) < average_amplitude
    ) {
      S_point = _.indexOf(s_segment_range, _.min(s_segment_range)) + 2;
      const s_segment = getRange(study.BWR, closestR, S_point);
      S_point = _.indexOf(s_segment, _.min(s_segment));
      const s_tend_segment_range = getRange(
        QRS_haft_segment_range,
        S_point,
        window_fiducial_point - S_point,
      );
      S_Tend = _.indexOf(s_tend_segment_range, _.max(s_tend_segment_range));
      S_point += closestR;

      const slope = (study.BWR[S_point + S_Tend] - study.BWR[S_point]) / S_Tend;
      let stend = 0;
      for (stend = S_point; stend < S_point + S_Tend; stend += 1) {
        if (
          ((study.BWR[S_point + S_Tend] - study.BWR[stend]) / (S_point + S_Tend - stend) < 0 &&
            slope > 0) ||
          ((study.BWR[S_point + S_Tend] - study.BWR[stend]) / (S_point + S_Tend - stend) > 0 &&
            slope < 0)
        ) {
          S_Tend = stend;
          break;
        } else if (
          Math.abs((study.BWR[S_point + S_Tend] - study.BWR[stend]) / (S_point + S_Tend - stend)) <
          0.0005
        ) {
          S_Tend = stend;
          break;
        }
      }

      if (stend === S_point + S_Tend) {
        S_Tend += S_point;
      }
    }

    /** *
     * Q Point Detection
     * */

    let q_segment_range = getRange(QRS_segment_range, 0, window_fiducial_point);

    if (
      study.EnhancementQRS[closestR] > 0 &&
      q_segment_range &&
      q_segment_range.length > 0 &&
      _.min(q_segment_range) < average_amplitude
    ) {
      const point_min = _.indexOf(q_segment_range, _.min(q_segment_range));
      let point_max = _.indexOf(
        getRange(q_segment_range, 0, point_min),
        _.max(getRange(q_segment_range, 0, point_min)),
      );

      const crest = _.max(
        getRange(study.BWR, closestR - window_fiducial_point + point_max, point_min - point_max),
      );

      const indexCrest = _.indexOf(
        getRange(study.BWR, closestR - window_fiducial_point + point_max, point_min - point_max),
        crest,
      );

      const crestAverage = getAverage(
        getRange(study.BWR, closestR - window_fiducial_point, window_fiducial_point * 2),
      );
      let crest_point = 0;
      if (
        indexCrest !== point_max &&
        crest > crestAverage * 0.85 &&
        crestAverage > 0 &&
        _.indexOf(
          getRange(study.BWR, closestR - window_fiducial_point + point_max, point_min - point_max),
          crest,
        ) > 0
      ) {
        crest_point = _.indexOf(
          getRange(study.BWR, closestR - window_fiducial_point + point_max, point_min - point_max),
          crest,
        );
        q_segment_range = getRange(
          study.BWR,
          closestR - window_fiducial_point,
          point_max + crest_point,
        );
        Q_point = _.indexOf(q_segment_range, _.min(q_segment_range));

        let tmp_index = 0;
        const area_triangle = [];
        for (tmp_index = Q_point + 1; tmp_index < point_max + crest_point - 1; tmp_index += 1) {
          const area =
            0.5 *
            Math.abs(
              (point_max + crest_point - Q_point) *
                (study.BWR[closestR - window_fiducial_point + tmp_index] -
                  study.BWR[closestR - window_fiducial_point + Q_point]) -
                (tmp_index - Q_point) *
                  (study.BWR[closestR - window_fiducial_point + point_max + crest_point] -
                    study.BWR[closestR - window_fiducial_point + Q_point]),
            );
          area_triangle.push(area);
        }
        if (area_triangle.length > 0 && _.max(area_triangle) > 0.27) {
          Q_point += _.indexOf(area_triangle, _.max(area_triangle));
        }
      } else {
        q_segment_range = getRange(
          study.BWR,
          closestR - window_fiducial_point + point_min,
          window_fiducial_point - point_min,
        );
        Q_point = _.indexOf(q_segment_range, _.min(q_segment_range)) + point_min;
      }

      // if (Q_point === 0) { return null; }

      if (point_max < Q_point) {
        Q_Tend = point_max;
      } else {
        q_segment_range = getRange(QRS_segment_range, 0, window_fiducial_point);
        point_max = _.indexOf(
          getRange(q_segment_range, 0, Q_point),
          _.max(getRange(q_segment_range, 0, Q_point)),
        );
        Q_Tend = point_max;
      }
      const slope =
        study.BWR[closestR - window_fiducial_point + Q_point] -
        study.BWR[closestR - window_fiducial_point + (Q_point - 1)];
      let qtend = 0;
      const area_tend_triangle = [];

      for (qtend = Q_point; qtend > Q_Tend + 1; qtend -= 2) {
        area_tend_triangle.push(
          study.BWR[closestR - window_fiducial_point + qtend] -
            study.BWR[closestR - window_fiducial_point + (qtend - 1)],
        );

        if (
          (study.BWR[closestR - window_fiducial_point + qtend] -
            study.BWR[closestR - window_fiducial_point + (qtend - 1)] >
            0 &&
            slope < 0) ||
          (study.BWR[closestR - window_fiducial_point + qtend] -
            study.BWR[closestR - window_fiducial_point + (qtend - 1)] <
            0 &&
            slope > 0)
        ) {
          break;
        } else if (
          Math.abs(
            study.BWR[closestR - window_fiducial_point + qtend] -
              study.BWR[closestR - window_fiducial_point + (qtend - 1)],
          ) < 0.0002
        ) {
          break;
        }
      }
      if (area_tend_triangle && area_tend_triangle.length > 0) {
        Q_Tend = Q_point - (area_tend_triangle.length + 1);
      }

      Q_point += closestR - window_fiducial_point;
      Q_Tend += closestR - window_fiducial_point;

      if (study.BWR[Q_Tend] < study.BWR[Q_point]) {
        Q_point = Q_Tend;
      }
    } else if (
      q_segment_range &&
      q_segment_range.length > 0 &&
      _.min(q_segment_range) < average_amplitude
    ) {
      const point_max = _.indexOf(q_segment_range, _.max(q_segment_range));
      let point_min = _.indexOf(
        getRange(q_segment_range, 0, point_max),
        _.min(getRange(q_segment_range, 0, point_max)),
      );

      q_segment_range = getRange(
        study.BWR,
        closestR - window_fiducial_point + point_min,
        window_fiducial_point - point_min,
      );
      Q_point = _.indexOf(q_segment_range, _.max(q_segment_range)) + point_min;

      // if (Q_point === 0) { return null; }

      if (point_min < Q_point) {
        Q_Tend = point_min;
      } else {
        q_segment_range = getRange(QRS_segment_range, 0, window_fiducial_point);
        point_min = _.indexOf(
          getRange(q_segment_range, 0, Q_point),
          _.min(getRange(q_segment_range, 0, Q_point)),
        );
        Q_Tend = point_min;
      }
      // const slope = (study.BWR[(closestR - window_fiducial_point) + (Q_point)] - study.BWR[(closestR - window_fiducial_point) + (Q_point - 1)]);
      // const area_tend_triangle = [];

      Q_point += closestR - window_fiducial_point;
      Q_Tend += closestR - window_fiducial_point;
    }

    /** *
     * P wave detection
     * */
    pointP = _.findLast(study.BlockPwave, (v) => v.start < pointX);

    if (pointP && pointP.start < beforeClosestR) {
      pointP = null;
    } else if (pointP) {
      // const window_xm = parseInt(0.2 * data.config.samplingRate as any, 10);
      let xm = -1;

      xm = pointP.start;

      let window_xr = parseInt((0.2 * data.config.samplingRate) as any, 10);
      let xr = -1;

      if (xm - window_xr < beforeClosestR) {
        window_xr /= 2;
      }

      const segment_xr = getRange(study.FirstDerivative, xm - window_xr, window_xr);
      //   const closestZero = segment_xr.OrderBy(item => Math.Abs(0.0 - item)).First();
      const closestZero = _.sortBy(segment_xr, [(item) => Math.abs(0.0 - item)])[0];
      xr = _.indexOf(segment_xr, closestZero);
      if (xr === 0) {
        xr = window_xr / 2;
      }
      xr += xm - window_xr;

      /** *
       * Calculation of the trapeziums areas of all the points located between “xm“ and “xr“.
       * 0.5*( ym − yi)*(2xr − xi − xm)
       * */

      const areas = [];

      for (let xi = xr; xi < xm; xi += 1) {
        areas.push(
          0.5 * Math.abs(study.Filter[xm] - study.Filter[xi]) * Math.abs(2 * xr - xi - xm),
        );
      }
      P_Tend = _.indexOf(areas, _.max(areas));
      P_Tend += xr;
    }

    /** *
     * T wave detection
     * */

    pointT = _.find(study.BlockTwave, (v) => v.start > pointX);

    if (pointT && pointT.start > afterClosestR) {
      pointT = null;
    } else if (pointT) {
      /** *
       * Determination of the point identified as "xm" located in the segment after the T
       * peak, which has a minimum(maximum) value in the first derivative, and be after the
       * maximum(minimum) for a T-wave positive(negative).For that, the algorithm searches
       * in a 200 ms window, starting from the T-wave peak(maximum or minimum).This
       * segment is appropriate to embrace the xm point in the final segment of the wave T */

      const window_xm = parseInt((0.2 * data.config.samplingRate) as any, 10);
      let xm = -1;
      const segment_xm = getRange(study.FirstDerivative, pointT.indexPeakWave, window_xm);
      if (pointT.AmplitudePWave > 0) {
        xm = pointT.indexPeakWave + _.indexOf(segment_xm, _.min(segment_xm));
      } else {
        xm = pointT.indexPeakWave + _.indexOf(segment_xm, _.max(segment_xm));
      }
      /** *
       * Determination of a point identifies as “xr“ located inside the isoelectric segment
       * and searched in a window between 200 ms and 400 ms, from the peak (maximum or
       * minimum) of the T-wave, preferably with a value of the first derivative near to zero. If
       * no point satisfies this condition, the central point is chosen.
       * Actually, the exact position of this point is not very important as long as it is beyond the T end point.
       * */
      let window_xr = parseInt((0.2 * data.config.samplingRate) as any, 10);
      let xr = -1;

      if (xm + window_xr >= afterClosestR) {
        window_xr /= 2;
      }

      const segment_xr = getRange(study.FirstDerivative, xm, window_xr);
      //   const closestZero = segment_xr.OrderBy(item => Math.Abs(0.0 - item)).First();
      const closestZero = _.sortBy(segment_xr, [(item) => Math.abs(0.0 - item)])[0];
      xr = _.indexOf(segment_xr, closestZero);
      if (xr === segment_xr.length - 1) {
        xr = window_xr / 2;
      }
      xr += xm;

      /** *
       * Calculation of the trapeziums areas of all the points located between “xm“ and “xr“.
       * 0.5*( ym − yi)*(2xr − xi − xm)
       * */

      const areas = [];

      for (let xi = xm; xi < xr; xi += 1) {
        if (pointT.AmplitudePWave > 0) {
          areas.push(0.5 * (study.Filter[xm] - study.Filter[xi]) * (2 * xr - xi - xm));
        } else {
          areas.push(0.5 * (study.Filter[xi] - study.Filter[xm]) * (2 * xr - xi - xm));
        }
      }
      T_Tend = _.indexOf(areas, _.max(areas));
      T_Tend += xm;
    }

    /** *
     * QRS interval
     * */

    R = closestR;
    Q = Q_Tend;
    S = S_Tend;
    P = P_Tend;

    if (beforeClosestR < R && P_Tend < 0) {
      P = beforeClosestR + (R - beforeClosestR) / 2;
    }
    T = T_Tend;

    if (afterClosestR > R && T_Tend < 0) {
      T = R + (afterClosestR - R) / 2;
    }

    const safeDistance = Math.round((lastIndex + S + startSampleDisplay) / 2);
    resultObj.P = Math.min(Math.round(P + startSampleDisplay), safeDistance);
    resultObj.Q = Math.min(Math.round(Q + startSampleDisplay), safeDistance);
    resultObj.R = Math.min(Math.round(R + startSampleDisplay), safeDistance);
    resultObj.S = Math.min(Math.round(S + startSampleDisplay), safeDistance);
    resultObj.T = Math.min(Math.round(T + startSampleDisplay), safeDistance);
    resultObj.R2 = Math.min(Math.round(afterClosestR + startSampleDisplay), safeDistance);
  }
  return resultObj;
};
